/**
 * ServWell's OWN mail on a shop's behalf (MAP 249 spec §5, §11, §11b): the reply address's
 * confirmation link, the sign-up confirmation, the test email, and the throttles every one of
 * them (and every team invite) passes. The words a refused click reads live here too, so the
 * server says them and the tests pin them.
 *
 * Not customer mail: no shop From name, no shop footer. Only invites also spend the shop's
 * ServWell-address limit (spec §3).
 */

// ── The throttles (spec §11b) ────────────────────────────────────────────────────────────

/** How long a per-address and per-person count looks back: the last 24 hours, rolling. */
export const THROTTLE_WINDOW_HOURS = 24;

/**
 * Emails to ONE address across every shop in `THROTTLE_WINDOW_HOURS`: reply-address
 * confirmations, team invites and sign-up confirmations. A typed address is anybody's, so a
 * stranger may not use ServWell to fill it.
 */
export const RECIPIENT_DAILY_LIMIT = 3;

/** Reply-address confirmations plus team invites, per shop, per shop-local day. */
export const PLATFORM_SHOP_DAILY_LIMIT = 10;

/** Test emails per person in `THROTTLE_WINDOW_HOURS`, across every shop they are in. */
export const TEST_EMAILS_PER_PERSON = 3;

// ── The links ────────────────────────────────────────────────────────────────────────────

/** A reply-address confirmation link (`/c/:token`) works this long, once. */
export const REPLY_LINK_TTL_HOURS = 24;

/** A sign-up confirmation link (`/v/:token`) works this long. */
export const EMAIL_CONFIRMATION_TTL_HOURS = 24;

// ── The reply address (spec §5) ──────────────────────────────────────────────────────────

/**
 * Local parts that never read a reply (spec §5 step 1, plus their common spellings): a reply
 * address is where a customer's answer must reach a person.
 */
export const ROLE_ADDRESS_LOCAL_PARTS = [
  'noreply',
  'no-reply',
  'donotreply',
  'do-not-reply',
  'postmaster',
  'mailer-daemon',
] as const;

export const ROLE_ADDRESS_REFUSAL =
  "Use an address someone reads: replies can't reach noreply@, no-reply@, donotreply@ or postmaster@.";

/** True for `noreply@…` and the rest of `ROLE_ADDRESS_LOCAL_PARTS` (any case). */
export function isRoleAddress(email: string): boolean {
  const at = email.lastIndexOf('@');
  const local = (at === -1 ? email : email.slice(0, at)).trim().toLowerCase();
  return (ROLE_ADDRESS_LOCAL_PARTS as readonly string[]).includes(local);
}

/** Where a shop's reply address stands, as Settings shows it. */
export interface ReplyAddressStatus {
  /** The CONFIRMED address customers' replies go to, or null. */
  confirmed: { email: string; confirmedAt: Date } | null;
  /**
   * The newest address typed and not yet confirmed (null when there is none, or it was
   * confirmed, replaced or cleared). `expired`: its link no longer works; send it again.
   */
  pending: { email: string; sentAt: Date; expiresAt: Date; expired: boolean } | null;
}

// ── What a refused click reads ───────────────────────────────────────────────────────────

export const PLATFORM_REFUSAL = {
  dark: "Email isn't switched on yet, so ServWell can't send the link.",
  paused:
    "ServWell has paused your shop's emails, so it can't send the link. Contact ServWell.",
  held: "Your shop's emails are on hold while ServWell reviews your shop name, so it can't send the link yet.",
  shopDaily: `Your shop has sent today's ${PLATFORM_SHOP_DAILY_LIMIT} confirmation and invite emails. Try again tomorrow.`,
  ceiling: "ServWell can't send any more email today. Try again tomorrow.",
  /** ServWell's confirmation emails for the UTC day are used (`CONFIRMATION_DAILY_ALLOWANCE`). */
  confirmations:
    'ServWell has sent all the confirmation emails it can today. Try again tomorrow.',
  testDaily: `You've sent today's ${TEST_EMAILS_PER_PERSON} test emails. Try again tomorrow.`,
  alreadyConfirmed: 'Your email address is already confirmed.',
} as const;

/** The address has had its `RECIPIENT_DAILY_LIMIT` emails from ServWell in the last 24 hours. */
export function recipientDailyRefusal(address: string): string {
  return `ServWell has already emailed ${address} ${RECIPIENT_DAILY_LIMIT} times today. Check its inbox and spam folder, or try again tomorrow.`;
}

/**
 * A send refused because no owner of the shop has confirmed their sign-up email (spec §11b).
 * `address`: the caller's own, when the caller is an owner who can confirm it; null for anyone
 * else (the office is bound by its owner's state).
 */
export function ownerUnconfirmedRefusal(address: string | null): string {
  return address
    ? `Confirm your email address to start sending. We emailed a link to ${address}; send it again if it hasn't arrived.`
    : "Your shop's owner needs to confirm their email address before the shop can send email. ServWell emailed them a link.";
}

/** The test email, to someone whose own address is not confirmed yet. */
export function testEmailUnconfirmedRefusal(address: string): string {
  return `Confirm your email address first: we emailed a link to ${address}.`;
}

// ── Side fixes (spec §12) ────────────────────────────────────────────────────────────────

/** The most shops one person may be in (Better Auth's `organizationLimit`). */
export const MAX_SHOPS_PER_PERSON = 3;

/** Accepting an invite that would be the person's 4th shop (`team.acceptInvite`). */
export function shopLimitJoinRefusal(shopName: string): string {
  return `You're already in ${MAX_SHOPS_PER_PERSON} shops, the most one person can have, so you can't join ${shopName}. Contact ServWell if you need another.`;
}

/** CreateOrgScreen's words for Better Auth's refusal of a 4th shop. */
export const SHOP_LIMIT_REFUSAL = `You're already in ${MAX_SHOPS_PER_PERSON} shops, the most one person can have. Contact ServWell if you need another.`;
